import React, { useEffect, useState, useContext } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';
import { AuthContext } from '../context/AuthContext';
import { toast } from 'react-toastify';
import {
    PlusIcon, CheckCircleIcon, ClipboardDocumentCheckIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';

const AppraisalDashboard = () => {
    const { user } = useContext(AuthContext);
    const [data, setData] = useState(null);
    const [kpis, setKpis] = useState([]);
    const [reviewMode, setReviewMode] = useState(false);
    const [selectedReview, setSelectedReview] = useState(null);

    // Form inputs for new KPI
    const [newKpiTitle, setNewKpiTitle] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const res = await api.get('/appraisals/dashboard');
            setData(res.data);

            // If user has existing appraisal, load it
            if (res.data.myKpis && res.data.myKpis.length > 0) {
                setKpis(res.data.myKpis);
            } else if (res.data.presets) {
                // Initialize with Presets
                setKpis(res.data.presets.map(p => ({
                    kpi_title: p.title,
                    kpi_description: p.description,
                    is_custom: false,
                    self_rating: 0,
                    self_comment: ''
                })));
            }
        } catch (err) {
            toast.error("Failed to load appraisal data");
        }
    };

    const handleAddCustomKPI = () => {
        if (!newKpiTitle) return;
        setKpis([...kpis, {
            kpi_title: newKpiTitle,
            kpi_description: 'Individual Goal',
            is_custom: true,
            self_rating: 0,
            self_comment: ''
        }]);
        setNewKpiTitle('');
    };

    const updateKpi = (index, field, value) => {
        const updated = [...kpis];
        updated[index][field] = value;
        setKpis(updated);
    };

    const handleSubmit = async () => {
        try {
            if (reviewMode && selectedReview) {
                // Manager Submitting Review
                await api.post('/appraisals/review', {
                    appraisal_id: selectedReview.id,
                    kpis: kpis,
                    action: 'Approve'
                });
                toast.success("Review Submitted");
                setReviewMode(false);
                setSelectedReview(null); // Reset selection
                fetchData();
            } else {
                // Employee Submitting Self-Review
                await api.post('/appraisals/save', {
                    cycle_id: data.activeCycle.id,
                    kpis: kpis,
                    status: 'Submitted'
                });
                toast.success("Appraisal Submitted to Manager");
                fetchData();
            }
        } catch (err) {
            toast.error("Submission Failed");
        }
    };

    // Logic to start reviewing a specific employee
    const startReview = async (review) => {
        try {
            // Set UI to review mode
            setSelectedReview(review);
            setReviewMode(true);

            // Fetch the KPIs for this specific appraisal ID
            const res = await api.get(`/appraisals/${review.id}`);
            setKpis(res.data);

            // Scroll to top to see the form
            window.scrollTo(0, 0);
        } catch (err) {
            toast.error("Could not load employee details");
            setReviewMode(false);
        }
    };

    if (!data) return <Layout><div className="p-8">Loading...</div></Layout>;

    return (
        <Layout>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-800">Performance Appraisal</h1>
                    <p className="text-slate-500">Cycle: {data.activeCycle?.name || 'No Active Cycle'}</p>
                </div>
            </div>

            {/* --- TABS / SECTIONS --- */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                {/* LEFT: FORM (Self or Review) */}
                <div className="lg:col-span-2 space-y-6">
                    {data.myAppraisal && data.myAppraisal.status !== 'Draft' && !reviewMode ? (
                        <div className="bg-emerald-50 border border-emerald-200 p-6 rounded-2xl flex items-center">
                            <CheckCircleIcon className="h-10 w-10 text-emerald-600 mr-4" />
                            <div>
                                <h3 className="font-bold text-emerald-800">Appraisal Submitted</h3>
                                <p className="text-emerald-600 text-sm">Status: {data.myAppraisal.status}. Waiting for {data.myAppraisal.approver_name}.</p>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100">
                            <h3 className="font-bold text-xl mb-6 border-b pb-4">
                                {reviewMode ? `Reviewing: ${selectedReview?.employee_name}` : 'Self Evaluation'}
                            </h3>

                            {kpis.map((k, idx) => (
                                <div key={idx} className="mb-8 p-4 bg-slate-50 rounded-xl border border-slate-100">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <h4 className="font-bold text-slate-800">{k.kpi_title}</h4>
                                            <p className="text-xs text-slate-500">{k.kpi_description}</p>
                                            {k.is_custom && <span className="bg-indigo-100 text-indigo-700 text-[10px] px-2 py-0.5 rounded-full">Individual Goal</span>}
                                        </div>
                                    </div>

                                    {/* Ratings Grid */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                        {/* Self Rating Section */}
                                        <div>
                                            <label className="text-xs font-bold text-slate-400 uppercase">Self Rating (1-5)</label>
                                            <div className="flex space-x-1 mt-1 mb-2">
                                                {[1, 2, 3, 4, 5].map(star => (
                                                    <button
                                                        key={star}
                                                        onClick={() => !reviewMode && updateKpi(idx, 'self_rating', star)}
                                                        disabled={reviewMode}
                                                        className={`${k.self_rating >= star ? 'text-amber-400' : 'text-slate-300'}`}
                                                    >
                                                        <StarSolid className="h-6 w-6" />
                                                    </button>
                                                ))}
                                            </div>
                                            <textarea
                                                disabled={reviewMode}
                                                className="w-full text-sm p-2 border rounded-lg bg-white"
                                                placeholder="Self comments..."
                                                value={k.self_comment || ''}
                                                onChange={(e) => updateKpi(idx, 'self_comment', e.target.value)}
                                            />
                                        </div>

                                        {/* Manager Rating Section (Visible if Review Mode or Completed) */}
                                        {(reviewMode || user.role !== 'Employee') && (
                                            <div>
                                                <label className="text-xs font-bold text-indigo-400 uppercase">Manager Rating</label>
                                                <div className="flex space-x-1 mt-1 mb-2">
                                                    {[1, 2, 3, 4, 5].map(star => (
                                                        <button
                                                            key={star}
                                                            onClick={() => reviewMode && updateKpi(idx, 'manager_rating', star)}
                                                            disabled={!reviewMode}
                                                            className={`${k.manager_rating >= star ? 'text-indigo-500' : 'text-slate-300'}`}
                                                        >
                                                            <StarSolid className="h-6 w-6" />
                                                        </button>
                                                    ))}
                                                </div>
                                                <textarea
                                                    disabled={!reviewMode}
                                                    className="w-full text-sm p-2 border rounded-lg bg-white"
                                                    placeholder="Manager remarks..."
                                                    value={k.manager_comment || ''}
                                                    onChange={(e) => updateKpi(idx, 'manager_comment', e.target.value)}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}

                            {/* Add Custom KPI (Only Employee in Draft) */}
                            {!reviewMode && (!data.myAppraisal || data.myAppraisal.status === 'Draft') && (
                                <div className="flex gap-2 mt-4 border-t pt-4">
                                    <input
                                        className="flex-1 p-2 border rounded-xl text-sm"
                                        placeholder="Add individual goal..."
                                        value={newKpiTitle}
                                        onChange={e => setNewKpiTitle(e.target.value)}
                                    />
                                    <button onClick={handleAddCustomKPI} className="p-2 bg-slate-900 text-white rounded-xl"><PlusIcon className="h-5 w-5" /></button>
                                </div>
                            )}

                            <div className="mt-8 flex justify-end">
                                <button
                                    onClick={handleSubmit}
                                    className="bg-indigo-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-indigo-700 shadow-lg"
                                >
                                    {reviewMode ? 'Submit Review' : 'Submit Self-Appraisal'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* RIGHT: PENDING REVIEWS (Manager/Admin Only) */}
                {(user.role === 'Manager' || user.role === 'Admin' || user.role === 'Super Admin') && (
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6 sticky top-8">
                            <h3 className="font-bold text-lg mb-4 flex items-center">
                                <ClipboardDocumentCheckIcon className="h-5 w-5 mr-2 text-indigo-500" />
                                Pending Reviews
                            </h3>
                            <div className="space-y-3">
                                {data.pendingReviews.length === 0 ? <p className="text-slate-400 text-sm">No pending reviews.</p> :
                                    data.pendingReviews.map(r => (
                                        <div key={r.id} className="p-4 border rounded-xl hover:bg-slate-50 cursor-pointer transition" onClick={() => startReview(r)}>
                                            <div className="font-bold text-slate-800">{r.employee_name}</div>
                                            <div className="text-xs text-slate-500">{r.department} • {r.status}</div>
                                            <div className="mt-2 text-xs font-bold text-indigo-600">Action Required</div>
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default AppraisalDashboard;